// Payload da página embutido pelo servidor (`public-blog-html`) em
// `<script id="fastblog-data" type="application/json">`.
//
// Quando o visitante chega por um domínio próprio, a página já traz o
// projeto + os dados daquela URL. O React usa isso como estado inicial e
// abre sem esqueleto e sem nova busca.

import type { PublicBlogMode, PublicBlogResponse } from "@/lib/public-blog-fetch";

const MAX_EMBEDDED_AGE_MS = 10 * 60 * 1000;

let parsed: PublicBlogResponse | null | undefined;

function stripWww(host: string): string {
  return (host || "").trim().toLowerCase().replace(/^www\./, "");
}

/** Caminho comparável: minúsculo, sem barra final, "/" para vazio. */
export function normalizeEmbeddedPath(pathname: string): string {
  let p = (pathname || "/").split("?")[0].split("#")[0].toLowerCase();
  p = p.replace(/\/{2,}/g, "/");
  if (p.length > 1) p = p.replace(/\/+$/, "");
  if (!p.startsWith("/")) p = `/${p}`;
  return p || "/";
}

/** Lê e interpreta o bloco uma única vez por carregamento de página. */
export function readEmbeddedBlogData(): PublicBlogResponse | null {
  if (parsed !== undefined) return parsed;
  parsed = null;
  if (typeof document === "undefined") return parsed;
  try {
    const el = document.getElementById("fastblog-data");
    const raw = el?.textContent;
    if (!raw) return parsed;
    const data = JSON.parse(raw) as PublicBlogResponse;
    if (!data || typeof data !== "object" || !data.project || !data.project.id) return parsed;
    if (data.generatedAt) {
      const age = Date.now() - new Date(data.generatedAt).getTime();
      if (Number.isFinite(age) && age > MAX_EMBEDDED_AGE_MS) return parsed;
    }
    parsed = data;
  } catch {
    parsed = null;
  }
  return parsed;
}

/** Payload embutido pertence a este host? (independe da rota) */
export function embeddedForCurrentHost(): PublicBlogResponse | null {
  const data = readEmbeddedBlogData();
  if (!data || typeof window === "undefined") return null;
  if (stripWww(data.host || "") !== stripWww(window.location.hostname)) return null;
  return data;
}

export interface EmbeddedMatch {
  mode: PublicBlogMode;
  page?: number;
  postSlug?: string;
  categorySlug?: string;
}

/**
 * Payload embutido para a página atual, ou null quando não bate com a
 * rota/página pedida (ex.: depois de uma navegação dentro do app).
 */
export function takeEmbeddedBlogData(expect: EmbeddedMatch): PublicBlogResponse | null {
  const data = embeddedForCurrentHost();
  if (!data) return null;
  if (data.mode !== expect.mode) return null;

  const currentPath = normalizeEmbeddedPath(window.location.pathname);
  const basePath = normalizeEmbeddedPath(data.path || "/");
  const page = Math.max(1, Number(data.page) || 1);
  const expectedPath = page > 1 ? `${basePath === "/" ? "" : basePath}/pagina/${page}` : basePath;
  if (currentPath !== expectedPath) {
    // Forma antiga: /?page=N na mesma listagem.
    const queryPage = Number(new URLSearchParams(window.location.search).get("page") || "") || 1;
    if (!(currentPath === basePath && queryPage === page)) return null;
  }

  if (expect.page && expect.page !== page) return null;
  if (expect.mode === "post") {
    const want = (expect.postSlug || "").toLowerCase();
    const got = (data.canonicalSegment || "").toLowerCase();
    const urlSlug = basePath.replace(/^\/+/, "").split("/")[0];
    if (want && want !== got && want !== urlSlug) return null;
  }
  if (expect.mode === "category") {
    const want = (expect.categorySlug || "").toLowerCase();
    const got = (data.categorySlug || "").toLowerCase();
    const urlSlug = basePath.replace(/^\/(?:category|categoria)\//, "").split("/")[0];
    if (want && want !== got && want !== urlSlug) return null;
  }
  return data;
}

/** Idade do payload embutido em ms (Infinity quando não há). */
export function embeddedAgeMs(): number {
  const data = readEmbeddedBlogData();
  if (!data?.generatedAt) return Number.POSITIVE_INFINITY;
  const age = Date.now() - new Date(data.generatedAt).getTime();
  return Number.isFinite(age) ? age : Number.POSITIVE_INFINITY;
}
